import { Alert } from "@/src/lib/alert";
import { router, type Href } from "expo-router";
import { signOut } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { BrandIntro } from "@/src/components/brand/BrandIntro";
import { PrimaryButton } from "@/src/components/ui/PrimaryButton";
import { ScreenContainer } from "@/src/components/ui/ScreenContainer";
import { useStartup } from "@/src/hooks/useStartup";
import {
  EMAIL_VERIFICATION_COOLDOWN_MS,
  emailVerificationErrorMessage,
  refreshEmailVerification,
  resendVerificationEmail,
  verificationCooldownSeconds,
} from "@/src/lib/emailVerification";
import { auth } from "@/src/lib/firebase";
import { spacing, text, typography } from "@/src/theme/tokens";

export default function VerifyEmailScreen() {
  const { user, status } = useStartup();
  const [busyAction, setBusyAction] = useState<"check" | "resend" | "signout" | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (resendAvailableAt <= Date.now()) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= resendAvailableAt) clearInterval(timer);
    }, 1_000);
    return () => clearInterval(timer);
  }, [resendAvailableAt]);

  const cooldown = useMemo(
    () => verificationCooldownSeconds(resendAvailableAt, now),
    [now, resendAvailableAt]
  );

  async function onCheck() {
    const current = auth.currentUser;
    if (!current) {
      Alert.alert("Session expirée", "Reconnecte-toi pour vérifier ton adresse email.");
      router.replace("/(auth)/login" as Href);
      return;
    }
    try {
      setBusyAction("check");
      const verified = await refreshEmailVerification(current);
      if (!verified) {
        Alert.alert("Email non vérifié", "Le lien n’a pas encore été validé. Ouvre l’email puis réessaie.");
      }
    } catch (error: unknown) {
      Alert.alert("Vérification impossible", emailVerificationErrorMessage(error));
    } finally {
      setBusyAction(null);
    }
  }

  async function onResend() {
    if (cooldown > 0) return;
    const current = auth.currentUser;
    if (!current) {
      Alert.alert("Session expirée", "Reconnecte-toi pour renvoyer l’email.");
      router.replace("/(auth)/login" as Href);
      return;
    }
    try {
      setBusyAction("resend");
      await resendVerificationEmail(current);
      const nextAvailableAt = Date.now() + EMAIL_VERIFICATION_COOLDOWN_MS;
      setNow(Date.now());
      setResendAvailableAt(nextAvailableAt);
      Alert.alert("Email envoyé", "Un nouveau lien de vérification vient d’être envoyé.");
    } catch (error: unknown) {
      Alert.alert("Envoi impossible", emailVerificationErrorMessage(error));
    } finally {
      setBusyAction(null);
    }
  }

  async function onSignOut() {
    try {
      setBusyAction("signout");
      await signOut(auth);
    } catch (error: unknown) {
      Alert.alert("Déconnexion impossible", emailVerificationErrorMessage(error));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <ScreenContainer contentStyle={styles.content}>
      <View style={styles.intro}>
        <BrandIntro compact />
        <View style={styles.headingGroup}>
          <Text style={styles.heading}>Vérifie ton adresse email</Text>
          <Text style={styles.email}>{user?.email ?? auth.currentUser?.email ?? "Adresse indisponible"}</Text>
          <Text style={styles.body}>Nous t’avons envoyé un lien de vérification.{"\n"}Pense à vérifier tes spams.</Text>
        </View>
      </View>

      <View style={styles.actions}>
        <PrimaryButton
          busy={busyAction === "check" || status === "loading"}
          busyLabel="Vérification…"
          label="J’ai vérifié mon email"
          onPress={() => void onCheck()}
        />
        <Pressable
          accessibilityRole="button"
          disabled={busyAction !== null || cooldown > 0}
          onPress={() => void onResend()}
          style={({ pressed }) => [styles.secondaryButton, (pressed || cooldown > 0) && styles.dimmed]}
        >
          <Text style={styles.secondaryLabel}>
            {cooldown > 0 ? `Renvoyer l’email (${cooldown} s)` : "Renvoyer l’email"}
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          disabled={busyAction !== null}
          onPress={() => void onSignOut()}
          style={styles.signOut}
        >
          <Text style={styles.signOutLabel}>Se déconnecter</Text>
        </Pressable>
      </View>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  content: { justifyContent: "center", gap: spacing.xxl },
  intro: { gap: spacing.xl },
  headingGroup: { gap: spacing.sm },
  heading: { color: text.primary, fontFamily: typography.family.semibold, fontSize: typography.size.heading, lineHeight: typography.lineHeight.heading },
  email: { color: text.brand, fontFamily: typography.family.semibold, fontSize: 16, lineHeight: 24 },
  body: { color: text.secondary, fontFamily: typography.family.regular, fontSize: typography.size.body, lineHeight: typography.lineHeight.body, maxWidth: 420 },
  actions: { gap: spacing.md },
  secondaryButton: { alignItems: "center", borderColor: text.brand, borderRadius: 12, borderWidth: 1, minHeight: 48, justifyContent: "center", paddingHorizontal: spacing.lg },
  secondaryLabel: { color: text.brand, fontFamily: typography.family.semibold, fontSize: 15 },
  signOut: { alignItems: "center", minHeight: 44, justifyContent: "center" },
  signOutLabel: { color: text.secondary, fontFamily: typography.family.semibold, fontSize: 15 },
  dimmed: { opacity: 0.55 },
});
